import React, { Dispatch, FC, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import moment from 'moment';

import { DataTable, Table, TBody, TElement, THead, TRow } from '@adeccoux/tag-ds';
import { SortingButton } from '@features/oneOnboardingAdmin/components/AdminDashboardTable/SortingButton';
import AdminPagination from '@features/oneOnboardingAdmin/components/AdminPagination';
import { useGetBulkMailListandUpdateStore } from '@hooks/apiHooks';
import { RootState } from '@store/rootReducer';
import { oneOnboardingSlice } from '@store/slices/oneOnboarding.slice';

import { columns, statusClassMap, StatusType } from '../EmailOverviewInterface';

import MailDetails from './MailDetails';

type pagination = {
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  total: number;
  rowsPerPage: number;
  setRowsPerPage: Dispatch<SetStateAction<number>>;
};

const EmailOverviewTable: FC = () => {
  const storeDispatch = useDispatch();
  const { t } = useTranslation(['recruiter', 'stepper']);
  const [page, setPage] = useState<number>(0);
  const [resultPerPage, setResultPerPage] = useState<number>(10);
  const [order, setOrder] = useState<string>('DESC');
  const [orderBy, setOrderBy] = useState<string>('createdAt');
  const [detailSidebarActive, setDetailSidebarActive] = useState(false);
  const [selectedBulkMail, setSelectedBulkMail] = useState();

  const handleMailDetail = (bulkEmailItem: any) => {
    setSelectedBulkMail(bulkEmailItem);
    setDetailSidebarActive(true);
  };

  const bulkEmailListData = useSelector((state: RootState) => state.oneOnboarding.bulkEmailListData);
  const { bulkMailList, totalRecords } = bulkEmailListData;
  const pagination: pagination = {
    page,
    setPage,
    total: totalRecords,
    rowsPerPage: resultPerPage,
    setRowsPerPage: setResultPerPage,
  };

  useGetBulkMailListandUpdateStore({
    page,
    resultPerPage,
    sort: orderBy,
    sortDir: order,
    refetchOnMount: true,
    callback: (data) => {
      storeDispatch(oneOnboardingSlice.actions.setBulkEmailList(data));
    },
  });

  const detailControls = {
    isActive: detailSidebarActive,
    selectedBulkMail: selectedBulkMail,
    onClose: () => setDetailSidebarActive(false),
    onOpen: () => setDetailSidebarActive(true),
  };

  return (
    <>
      <DataTable className="tag-ds ">
        <Table>
          <THead>
            <TRow>
              {columns.map((column) => {
                return column.sorting ? (
                  <TElement key={column.field}>
                    <div className="sortable-wrapper" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <SortingButton
                        onClick={() => {
                          setOrder(orderBy === column.field && order === 'asc' ? 'desc' : 'asc');
                          setOrderBy(column.field);
                        }}
                        direction={orderBy === column.field ? order : 'asc'}
                        active={orderBy === column.field}
                      />
                      <span
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          setOrder(orderBy === column.field && order === 'asc' ? 'desc' : 'asc');
                          setOrderBy(column.field);
                        }}
                      >
                        {t(column.headerName)}
                      </span>
                    </div>
                  </TElement>
                ) : (
                  <TElement key={column.field}>{t(column.headerName)}</TElement>
                );
              })}
            </TRow>
          </THead>
          <TBody>
            {bulkMailList?.length &&
              bulkMailList.map((bulkEmailItem) => {
                const status = bulkEmailItem.status as StatusType;
                const statusClass = statusClassMap[status];
                return (
                  <TRow key={bulkEmailItem.id}>
                    <TElement>{moment(bulkEmailItem.createdAt).format('DD.MM.YYYY')}</TElement>
                    <TElement>{bulkEmailItem.templateName}</TElement>
                    <TElement>{bulkEmailItem.candidates}</TElement>
                    <TElement>
                      <div className={`status-tag_${statusClass} `} style={{ marginBottom: 8 }}>
                        {bulkEmailItem.status}
                      </div>
                    </TElement>
                    <TElement>
                      <button
                        className="button-secondary_small"
                        onClick={() => {
                          handleMailDetail(bulkEmailItem);
                        }}
                      >
                        <span className="material-icons">notes</span>
                      </button>
                    </TElement>
                  </TRow>
                );
              })}
          </TBody>
        </Table>
      </DataTable>
      {bulkMailList.length > 0 && (
        <AdminPagination
          rowsPerPage={pagination.rowsPerPage}
          setRowsPerPage={pagination.setRowsPerPage}
          total={pagination.total}
          currentPage={pagination.page}
          setPage={pagination.setPage}
          isRowsPerPageVisible={true}
        />
      )}
      <MailDetails detailControls={detailControls} />
    </>
  );
};

export default EmailOverviewTable;
